import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Check, X, Clock, Search, ChevronDown, ChevronUp, Phone, Mail, FileText } from 'lucide-react';

const statusColors = { pending: 'bg-yellow-500/20 text-yellow-300', confirmed: 'bg-blue-500/20 text-blue-300', completed: 'bg-green-500/20 text-green-300', cancelled: 'bg-red-500/20 text-red-300' };

export default function AdminBookings() {
  const { apiClient } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchBookings = useCallback(async () => {
    try { const { data } = await apiClient.get('/api/admin/bookings'); setBookings(data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try { await apiClient.patch(`/api/admin/bookings/${id}/status`, { status }); toast.success(`Booking ${status}`); fetchBookings(); }
    catch { toast.error('Failed'); }
  };

  const filtered = bookings.filter(b =>
    b.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.intake_reason?.toLowerCase().includes(search.toLowerCase()) ||
    b.email?.toLowerCase().includes(search.toLowerCase())
  );

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div><h1 className="text-2xl font-heading font-bold text-white">Bookings</h1><p className="text-white/50 text-sm">{bookings.length} total</p></div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Search patient, email, reason..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 w-72" data-testid="admin-bookings-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">No bookings found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => (
            <div key={b.id} className="rounded-2xl border border-white/8 bg-white/[0.03] overflow-hidden" data-testid={`admin-booking-${b.id}`}>
              {/* Summary Row */}
              <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div>
                    <p className="text-white font-bold text-sm">{b.patient_name}</p>
                    <p className="text-white/40 text-xs">{b.intake_reason || 'No reason specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusColors[b.status] || 'bg-white/10 text-white/50'}`}>{b.status}</span>
                  <span className="text-white/30 text-xs">{new Date(b.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1.5">
                    {b.status === 'pending' && (<><button onClick={e => { e.stopPropagation(); updateStatus(b.id, 'confirmed'); }} className="p-1.5 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30" title="Confirm" data-testid={`confirm-${b.id}`}><Check size={14} /></button><button onClick={e => { e.stopPropagation(); updateStatus(b.id, 'cancelled'); }} className="p-1.5 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30" title="Cancel" data-testid={`cancel-${b.id}`}><X size={14} /></button></>)}
                    {b.status === 'confirmed' && <button onClick={e => { e.stopPropagation(); updateStatus(b.id, 'completed'); }} className="p-1.5 rounded-lg bg-green-500/20 text-green-300 hover:bg-green-500/30" title="Complete"><Check size={14} /></button>}
                  </div>
                  {expanded === b.id ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
                </div>
              </div>

              {/* Expanded Details */}
              {expanded === b.id && (
                <div className="border-t border-white/5 p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                  {/* Patient Info */}
                  <div>
                    <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Patient Information</h4>
                    <div className="space-y-2">
                      <p className="text-white"><span className="text-white/40">Name:</span> {b.patient_name}</p>
                      <p className="text-white"><span className="text-white/40">DOB:</span> {b.patient_dob || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Gender:</span> {b.patient_gender || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Guardian:</span> {b.guardian_name || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Address:</span> {b.legal_address || 'N/A'}</p>
                      <div className="flex gap-3 mt-2">
                        {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"><Phone size={12} />{b.phone}</a>}
                        {b.email && <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"><Mail size={12} />{b.email}</a>}
                      </div>
                    </div>
                  </div>

                  {/* Insurance */}
                  <div>
                    <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Insurance / Payer</h4>
                    <div className="space-y-2">
                      <p className="text-white"><span className="text-white/40">Insured Name:</span> {b.insured_name || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Insured DOB:</span> {b.insured_dob || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Insured Gender:</span> {b.insured_gender || 'N/A'}</p>
                      <div className="mt-3 space-y-2">
                        {b.insurance_front_path && <a href={`${API_URL}/api/files/${b.insurance_front_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 text-xs"><FileText size={12} />Insurance Card (Front)</a>}
                        {b.insurance_back_path && <a href={`${API_URL}/api/files/${b.insurance_back_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 text-xs"><FileText size={12} />Insurance Card (Back)</a>}
                      </div>
                    </div>
                  </div>

                  {/* Intake & Referral */}
                  <div>
                    <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Intake & Referral</h4>
                    <div className="space-y-2">
                      <p className="text-white"><span className="text-white/40">Reason:</span> {b.intake_reason || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Referral:</span> {b.referral_source || 'N/A'}</p>
                      <p className="text-white"><span className="text-white/40">Availability:</span> {(b.availability || []).join(', ') || 'N/A'}{b.availability_other ? ` (${b.availability_other})` : ''}</p>
                      {b.additional_notes && <div className="mt-3"><span className="text-white/40">Notes:</span><p className="text-white mt-1 bg-white/5 p-2 rounded-lg">{b.additional_notes}</p></div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
